import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/* -------------------------------- Heading -------------------------------- */

const headingVariants = cva("font-display font-semibold tracking-tight text-text-heading", {
    variants: {
        level: {
            1: "text-4xl lg:text-5xl font-bold",
            2: "text-3xl font-bold",
            3: "text-2xl font-semibold",
            4: "text-xl font-semibold",
            5: "text-lg font-medium",
            6: "text-base font-medium",
        },
    },
    defaultVariants: {
        level: 1,
    },
});

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof headingVariants> {
    level?: HeadingLevel;
    as?: `h${HeadingLevel}`;
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(({ className, level = 1, as, ...props }, ref) => {
    const Tag = as ?? (`h${level}` as const);
    return <Tag ref={ref} className={cn(headingVariants({ level, className }))} {...props} />;
});
Heading.displayName = "Heading";

/* --------------------------------- Text ---------------------------------- */

const textVariants = cva("font-sans text-text-body", {
    variants: {
        size: {
            "2xs": "text-[0.6875rem]", // 11px
            xs: "text-xs", // 12px
            sm: "text-sm", // 14px
            base: "text-base", // 16px
            lg: "text-lg", // 18px
            xl: "text-xl", // 20px
        },
        muted: {
            true: "text-text-muted",
        },
        weight: {
            normal: "font-normal",
            medium: "font-medium",
            semibold: "font-semibold",
            bold: "font-bold",
        },
    },
    defaultVariants: {
        size: "base",
        weight: "normal",
    },
});

export interface TextProps extends React.HTMLAttributes<HTMLParagraphElement>, VariantProps<typeof textVariants> {
    as?: "p" | "span" | "div";
}

const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
    ({ className, size, muted, weight, as: Tag = "p", ...props }, ref) => (
        <Tag ref={ref} className={cn(textVariants({ size, muted, weight, className }))} {...props} />
    ),
);
Text.displayName = "Text";

/* --------------------------------- Mono ---------------------------------- */

const monoVariants = cva("font-mono text-text-eyebrow", {
    variants: {
        size: {
            "2xs": "text-[0.6875rem]", // 11px
            xs: "text-xs", // 12px
            sm: "text-sm", // 14px
            base: "text-base", // 16px
            lg: "text-lg", // 18px
            xl: "text-xl", // 20px
        },
        muted: {
            true: "text-text-muted",
        },
        uppercase: {
            true: "uppercase",
        },
        weight: {
            normal: "font-normal",
            medium: "font-medium",
            semibold: "font-semibold",
            bold: "font-bold",
        },
        badge: {
            true: "inline-block w-fit bg-secondary/15 text-text-link rounded px-2 py-0.5",
        },
    },
    defaultVariants: {
        size: "base",
        weight: "normal",
    },
});

export interface MonoProps extends React.HTMLAttributes<HTMLParagraphElement>, VariantProps<typeof monoVariants> {
    as?: "p" | "span" | "div";
}

const Mono = React.forwardRef<HTMLParagraphElement, MonoProps>(
    ({ className, size, muted, weight, uppercase, badge, as: Tag = "p", ...props }, ref) => (
        <Tag ref={ref} className={cn(monoVariants({ size, muted, weight, uppercase, badge, className }))} {...props} />
    ),
);
Mono.displayName = "Mono";

/* --------------------------------- Link ---------------------------------- */

const linkVariants = cva(
    "font-sans underline underline-offset-4 transition-colors hover:text-text-link/80 text-text-link",
    {
        variants: {
            size: {
                xs: "text-xs", // 12px
                sm: "text-sm", // 14px
                base: "text-base", // 16px
                lg: "text-lg", // 18px
            },
            muted: {
                true: "text-text-muted",
            },
        },
        defaultVariants: {
            size: "base",
        },
    },
);

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof linkVariants> {}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(({ className, size, muted, ...props }, ref) => (
    <a ref={ref} className={cn(linkVariants({ size, muted, className }))} {...props} />
));
Link.displayName = "Link";

/* --------------------------------- Eyebrow -------------------------------- */

export interface EyebrowProps extends React.HTMLAttributes<HTMLParagraphElement> {
    as?: "p" | "span" | "div";
}

const Eyebrow = React.forwardRef<HTMLParagraphElement, EyebrowProps>(({ className, as: Tag = "p", ...props }, ref) => (
    <Tag
        ref={ref}
        className={cn("text-text-eyebrow font-mono text-xs font-medium tracking-widest uppercase", className)}
        {...props}
    />
));
Eyebrow.displayName = "Eyebrow";

/* --------------------------------- Body ----------------------------------- */

export interface BodyProps extends React.HTMLAttributes<HTMLParagraphElement> {
    as?: "p" | "span" | "div";
}

const Body = React.forwardRef<HTMLParagraphElement, BodyProps>(({ className, as: Tag = "p", ...props }, ref) => (
    <Tag ref={ref} className={cn("text-text-body font-sans text-base font-normal", className)} {...props} />
));
Body.displayName = "Body";

/* --------------------------------- Muted ---------------------------------- */

export interface MutedProps extends React.HTMLAttributes<HTMLParagraphElement> {
    as?: "p" | "span" | "div";
}

const Muted = React.forwardRef<HTMLParagraphElement, MutedProps>(({ className, as: Tag = "p", ...props }, ref) => (
    <Tag ref={ref} className={cn("text-text-muted font-sans text-sm font-normal", className)} {...props} />
));
Muted.displayName = "Muted";

/* --------------------------------- Caption -------------------------------- */

export interface CaptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
    as?: "p" | "span" | "div";
}

const Caption = React.forwardRef<HTMLParagraphElement, CaptionProps>(({ className, as: Tag = "p", ...props }, ref) => (
    <Tag ref={ref} className={cn("text-text-caption font-sans text-[0.6875rem] font-normal", className)} {...props} />
));
Caption.displayName = "Caption";

/* --------------------------------- Code ---------------------------------- */

export interface CodeProps extends React.HTMLAttributes<HTMLElement> {}

const Code = React.forwardRef<HTMLElement, CodeProps>(({ className, ...props }, ref) => (
    <code
        ref={ref}
        className={cn("bg-muted inline-block w-fit rounded px-[0.3rem] py-[0.2rem] font-mono text-sm", className)}
        {...props}
    />
));
Code.displayName = "Code";

export {
    Heading,
    headingVariants,
    Text,
    textVariants,
    Mono,
    monoVariants,
    Eyebrow,
    Body,
    Muted,
    Caption,
    Link,
    linkVariants,
    Code,
};
